import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle, Users } from "lucide-react";

interface Invitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  accepted_at: string | null;
  org_id: string;
  family_id: string;
  families: { name: string; onboarding_status: string | null } | null;
  orgs: { name: string } | null;
}

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = searchParams.get("token");

  const [isNewUser, setIsNewUser] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Fetch invitation details
  const { data: invitation, isLoading: inviteLoading, error: inviteError } = useQuery({
    queryKey: ["invitation", token],
    queryFn: async () => {
      if (!token) throw new Error("No token provided");

      const { data, error } = await supabase
        .from("family_invitations")
        .select(`
          id,
          email,
          role,
          expires_at,
          accepted_at,
          org_id,
          family_id,
          families:family_id (name, onboarding_status),
          orgs:org_id (name)
        `)
        .eq("token", token)
        .single();

      if (error) throw error;
      return data as Invitation;
    },
    enabled: !!token,
  });

  // Check if user already exists
  const { data: existingUser, isLoading: userLoading } = useQuery({
    queryKey: ["existing-user", invitation?.email],
    queryFn: async () => {
      if (!invitation?.email) return null;

      // Check if current user is signed in with this email
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email?.toLowerCase() === invitation.email.toLowerCase()) {
        return user;
      }

      // User might need to sign in or sign up
      return null;
    },
    enabled: !!invitation?.email,
  });

  // Check current auth state
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && invitation) {
        if (user.email?.toLowerCase() !== invitation.email.toLowerCase()) {
          // User is signed in with different email
          toast({
            title: "Wrong account",
            description: `Please sign out and use the account with email ${invitation.email}`,
            variant: "destructive",
          });
        }
      } else if (!user && invitation) {
        setIsNewUser(true);
      }
    };
    checkAuth();
  }, [invitation, toast]);

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!invitation) throw new Error("No invitation");

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Mark invitation as accepted and store user_id
      const { error: acceptError } = await supabase
        .from("family_invitations")
        .update({
          accepted_at: new Date().toISOString(),
          accepted_by: user.id
        })
        .eq("id", invitation.id);

      if (acceptError) throw acceptError;

      // Add user to org with OrgViewer role (basic access)
      const { error: orgRoleError } = await supabase
        .from("users_org_roles")
        .insert({
          user_id: user.id,
          org_id: invitation.org_id,
          role: "OrgViewer",
        });

      if (orgRoleError && !orgRoleError.message.includes("duplicate")) {
        console.error("Failed to add org role:", orgRoleError);
        // Don't throw - continue with family role assignment
      }

      // Add user to family with the specified role
      const { error: familyRoleError } = await supabase
        .from("users_family_roles")
        .insert({
          user_id: user.id,
          family_id: invitation.family_id,
          role: invitation.role,
        });

      if (familyRoleError && !familyRoleError.message.includes("duplicate")) {
        throw familyRoleError;
      }

      return {
        familyId: invitation.family_id,
        familyStatus: invitation.families?.onboarding_status,
      };
    },
    onSuccess: (result) => {
      toast({
        title: "Welcome!",
        description: `You've joined ${invitation?.families?.name || "the family"}.`,
      });

      // Route based on family's onboarding status
      const status = result.familyStatus;
      if (status === "kyc_pending") {
        // Family needs KYC - send to KYC wizard
        navigate(`/onboarding/kyc/${result.familyId}`);
      } else if (status === "data_pending") {
        // KYC complete but needs data - go to portfolio import
        navigate("/onboarding/portfolio-import");
      } else {
        // Status is 'active', 'draft', or null - go to dashboard
        navigate("/dashboard");
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to accept invitation",
        variant: "destructive",
      });
    },
  });

  // Sign up mutation for new users
  const signUpMutation = useMutation({
    mutationFn: async () => {
      if (!invitation) throw new Error("No invitation");
      if (password !== confirmPassword) throw new Error("Passwords do not match");
      if (password.length < 12) throw new Error("Password must be at least 12 characters");

      const { data, error } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: {
            invited_via: "family_invitation",
          },
        },
      });

      if (error) throw error;
      if (!data.user) throw new Error("Failed to create account");

      return data.user;
    },
    onSuccess: () => {
      toast({
        title: "Account created!",
        description: "Please check your email to verify your account, then return here to accept the invitation.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create account",
        variant: "destructive",
      });
    },
  });

  const isLoading = inviteLoading || userLoading;

  // No token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>
              This invitation link is invalid. Please check your email for the correct link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Invitation not found or error
  if (inviteError || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Invitation Not Found</CardTitle>
            <CardDescription>
              This invitation could not be found. It may have been revoked or already used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already accepted
  if (invitation.accepted_at) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <CardTitle>Already Accepted</CardTitle>
            <CardDescription>
              This invitation has already been accepted. You can sign in to access your account.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired
  if (new Date(invitation.expires_at) < new Date()) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <CardTitle>Invitation Expired</CardTitle>
            <CardDescription>
              This invitation has expired. Please contact your administrator for a new invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate("/auth")}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const roleName = {
    FamilyAdmin: "Family Admin",
    FamilyManager: "Family Manager",
    FamilyViewer: "Family Viewer",
  }[invitation.role] || invitation.role;

  // Show sign up form for new users
  if (isNewUser && !existingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <Users className="h-12 w-12 text-primary mx-auto mb-4" />
            <CardTitle>Create Your Account</CardTitle>
            <CardDescription>
              You've been invited to join <strong>{invitation.families?.name}</strong> as a {roleName}.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={invitation.email} disabled />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => signUpMutation.mutate()}
              disabled={signUpMutation.isPending}
            >
              {signUpMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating Account...
                </>
              ) : (
                "Create Account"
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Already have an account?{" "}
              <Button variant="link" className="p-0 h-auto" onClick={() => navigate("/auth")}>
                Sign in
              </Button>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Existing user - just accept
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <Users className="h-12 w-12 text-primary mx-auto mb-4" />
          <CardTitle>You're Invited!</CardTitle>
          <CardDescription>
            You've been invited to join <strong>{invitation.families?.name}</strong> on Vintage.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Organization</span>
              <span className="font-medium">{invitation.orgs?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Family</span>
              <span className="font-medium">{invitation.families?.name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Your Role</span>
              <span className="font-medium">{roleName}</span>
            </div>
          </div>
          <Button
            className="w-full"
            onClick={() => acceptMutation.mutate()}
            disabled={acceptMutation.isPending}
          >
            {acceptMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Accepting...
              </>
            ) : (
              "Accept Invitation"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
